import { buildNotificationResourceTypeLabels } from "settings/account/types/tag-resource-types";

export interface NotificationGroup {
  id: string;
  tenantId: number;
  name: string;
  /**
   * CSS design-token string (e.g. `"var(--color-semantic-bg-tag-red)"`).
   * The API stores a semantic name (`"red"`); mapped in `requests/request.tsx`
   * via Tags Manager `colorNameToToken` / `tokenToColorName`.
   */
  color: string | null;
  description: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  /** When set, resource assignments can mirror a Tags Manager tag (recipients are separate). */
  linkedTagId?: string | null;
  memberCount?: number;
  assignmentCount?: number;
  members?: GroupMember[];
  assignments?: GroupAssignment[];
  /**
   * Materialized tag resources returned by `GET /groups/:id` when `linkedTagId`
   * is set. Used to hydrate the Resources step on edit without a separate
   * Tags Manager fetch.
   */
  tagResources?: GroupTagResource[];
}

/** Row from `GET /groups/:id` → `tagResources` (Tags API wire `resourceType`). */
export interface GroupTagResource {
  id: number | string;
  tagId: number | string;
  module: string;
  resourceType: string;
  resourceId: number | string;
  resourceLabel?: string | null;
  /** Live secondary id from Tags API (e.g. subscription UUID / portId). */
  subtitle?: string | null;
  createdAt?: string;
}

export type GroupMemberType = "user" | "email";

export interface GroupMember {
  id: string;
  groupId: string;
  memberType: GroupMemberType;
  /** Auth0 sub (e.g. "auth0|abc"); null for external email-only members */
  auth0Id: string | null;
  /** Always populated; for internal users it's the denormalized Auth0 email */
  email: string;
  displayName: string | null;
  /** Ready-to-render label computed by the backend (displayName or email) */
  displayLabel: string;
  createdAt: string;
}

export type AssignableResourceType =
  | "port"
  | "cloud-connectivity"
  | "datacenter-interconnect"
  | "carrier-bridge"
  | "sdwan-hub"
  | "fwaas"
  | "backbone"
  | "bb-connector"
  | "vrf"
  | "internet"
  | "bgp-session"
  | "reflector"
  | "cluster"
  | "rv1-instance"
  | "rv1-bgp-neighbor"
  | "rv1-license";

export interface GroupAssignment {
  id: string;
  groupId: string;
  tenantId: number;
  resourceType: AssignableResourceType | string;
  resourceId: string;
  /** NULL means: fire for ALL events on the resource. Array = only those event types. */
  eventTypes: string[] | null;
  createdAt: string;
  /** UI-only: label from tag-linked draft before API resolves resource. */
  resourceDisplayHint?: string | null;
  /** UI-only: subscription UUID / subid from tag projection (Tags API `subtitle`). */
  resourceSubtitle?: string | null;
}

/** Local-only row for the create wizard before `POST /groups` exists. */
export interface DraftResourceAssignment {
  resourceType: string;
  resourceId: string;
  eventTypes: string[] | null;
  /** Tag projection label — shown when linked to a tag (no API resource lookup). */
  resourceDisplayHint?: string | null;
  /** Tag projection secondary id (UUID) — Tags API `subtitle`. */
  resourceSubtitle?: string | null;
}

export interface Recipient {
  kind: "user" | "email";
  /** Auth0 sub for internal users; null for external email-only */
  auth0Id: string | null;
  /** May be null for recipients that come ONLY from UserNotificationPreference (which doesn't yet denormalize email). Always populated for group/sub recipients. */
  email: string | null;
  displayName: string | null;
  viaGroups: { id: string; name: string }[];
  viaSelfService: boolean;
  viaUserPreference: boolean;
}

export type ServiceSubscriberType = "user" | "email";

export interface ServiceSubscription {
  id: string;
  tenantId: number;
  resourceType: string;
  resourceId: string;
  subscriberType: ServiceSubscriberType;
  /** Auth0 sub for internal users; null for external email-only */
  auth0Id: string | null;
  /** Always populated */
  email: string;
  displayName: string | null;
  displayLabel: string;
  eventTypes: string[] | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Shape accepted by POST /groups/:groupId/members.
 * For "user" type the frontend supplies auth0Id + email + optional displayName
 * (typically picked from getAuth0Users response, which has all three).
 */
export type AddMemberInput =
  | {
      type: "user";
      auth0Id: string;
      email: string;
      displayName?: string;
      /** Chopper tenant the user picker was scoped to — UI/tab grouping only; stripped before API. */
      pickerTenantId?: number;
    }
  | { type: "email"; email: string; displayName?: string };

export type AddSubscriptionInput =
  | {
      type: "user";
      auth0Id: string;
      email: string;
      displayName?: string;
      eventTypes?: string[] | null;
    }
  | {
      type: "email";
      email: string;
      displayName?: string;
      eventTypes?: string[] | null;
    };

/**
 * Singular labels — derived from the `tag-resource-types.ts` catalog, plus the
 * notification-only resource types that catalog cannot cover.
 */
export const RESOURCE_TYPE_LABELS: Record<string, string> = {
  ...buildNotificationResourceTypeLabels(),
  /**
   * CTX-19. Not a taggable resource, so it never comes from the tag catalog:
   * it exists only as a NotificationsV2 routing target for subscription
   * lifecycle events (expiry milestones). Adding it to TAG_RESOURCE_TYPES
   * would leak it into the Tags Manager pickers.
   */
  subscription: "Subscription",
};
